/**
 * @file ShipmentEventsSection.tsx
 * @description История статусов отгрузки в карточке (phase19.13 шаг 6).
 *
 * Тоггл + лента событий перевозчика. Лента грузится лениво при раскрытии:
 * у заказа бывает несколько отгрузок, и тянуть историю всех ради свёрнутого
 * блока — лишние запросы.
 *
 * Раскрываем сами, если доставка пошла не так (не вручено / возврат) или
 * отгрузка помечена зависшей: такие случаи менеджер обязан увидеть без клика.
 */

import { ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

import { ShipmentEventsFeed } from "./ShipmentEventsFeed";
import type { ShipmentEventsState } from "./useShipmentEvents";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { formatShopDateTime } from "../../lib/shop-time";

interface Props {
  events: ShipmentEventsState;
  /** Раскрыть сразу: проблемный этап или бейдж «зависла». */
  autoOpen: boolean;
  /** Отгрузка ещё в пути — для неё имеет смысл диагностика вебхука. */
  active: boolean;
  /** Ссылка на настройки перевозчика — для подсказки про вебхук. */
  carrierSettingsHref: string;
  /** «Обновить статус» из пустого состояния (та же кнопка, что в действиях). */
  onSync?: () => void;
  /** Действие над отгрузкой в процессе — кнопки блокируем. */
  acting?: boolean;
}

export function ShipmentEventsSection({
  events,
  autoOpen,
  active,
  carrierSettingsHref,
  onSync,
  acting = false,
}: Props) {
  const [open, setOpen] = useState(false);

  // Автораскрытие только «наружу»: если менеджер свернул ленту руками,
  // повторный расчёт бейджа не должен открывать её снова.
  const [autoApplied, setAutoApplied] = useState(false);
  useEffect(() => {
    if (autoOpen && !autoApplied) {
      setAutoApplied(true);
      setOpen(true);
    }
  }, [autoOpen, autoApplied]);

  const { load } = events;
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Счётчик известен уже после зонда — ждать раскрытия ленты не нужно.
  const count = events.total > 0 || events.loaded ? events.total : null;
  const last = events.lastEvent;

  return (
    <div className="mt-3 w-full border-t border-outline-variant pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-12 w-full items-center gap-1.5 rounded-md px-1 text-left text-sm text-on-surface-variant hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        {open ? (
          <ChevronDown className="size-4 shrink-0" />
        ) : (
          <ChevronRight className="size-4 shrink-0" />
        )}
        <span className="min-w-0 break-words">
          История статусов
          {count !== null ? ` · ${count}` : ""}
          {last ? ` — последнее ${formatShopDateTime(last.occurredAt)}` : ""}
        </span>
      </button>

      {open ? (
        <div className="pt-2">
          {events.loading && !events.loaded ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="size-5 animate-spin text-on-surface-variant" />
            </div>
          ) : events.error ? (
            <Alert severity="warning" title="История статусов не загрузилась">
              <div className="flex flex-col items-start gap-2">
                <span>{events.error}</span>
                <Button size="sm" variant="outline" className="min-h-11" onClick={events.reload}>
                  Повторить
                </Button>
              </div>
            </Alert>
          ) : events.items.length === 0 ? (
            <EmptyFeed onSync={onSync} acting={acting} />
          ) : (
            <ShipmentEventsFeed
              items={events.items}
              active={active}
              carrierSettingsHref={carrierSettingsHref}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Событий нет: объясняем, откуда они возьмутся, и даём кнопку опроса. */
function EmptyFeed({ onSync, acting }: { onSync?: () => void; acting: boolean }) {
  return (
    <div className="flex flex-col items-start gap-2 py-4 text-sm text-on-surface-variant">
      <p>Событий от перевозчика ещё нет. История появится после следующего обновления.</p>
      {onSync ? (
        <Button
          size="sm"
          variant="outline"
          className="min-h-11"
          onClick={onSync}
          disabled={acting}
        >
          <RefreshCw className="mr-1 size-4" />
          Обновить статус
        </Button>
      ) : null}
    </div>
  );
}
